import type { PurchaseVariantLookup, VariantLookup } from '@sahab/contracts'
import { keepPreviousData, queryOptions, type UseQueryResult, useQuery } from '@tanstack/react-query'
import { Command } from 'cmdk'
import { ChevronsUpDown, ScanBarcode, Search } from 'lucide-react'
import { Popover } from 'radix-ui'
import { type KeyboardEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '../../lib/api'
import { cn } from '../../lib/cn'
import type { Language } from '../../lib/format'
import { useLanguage } from '../../lib/i18n'
import { Amount } from '../../ui/amount'
import { useFieldControl } from '../../ui/field'
import { controlBase } from '../../ui/input'
import { usePickerKeyboard } from '../../ui/picker-keyboard'
import { floatingPanel, menuItem } from '../../ui/select'

export const variantLookupKeys = {
  all: ['product-variants', 'lookup'] as const,
  search: (q: string, limit: number, customerId: string | null = null, date: string | null = null) =>
    [...variantLookupKeys.all, q, limit, customerId, date] as const,
}

/** A document's customer and date: the lookup then brings that customer's custom prices (ADR-045). */
export interface LookupPriceFor {
  customerId: string | null
  date: string | null
}

/**
 * GET /product-variants/lookup: name, SKU or barcode (a pack's barcode too); exact matches come first.
 * Each row carries its units and price grid, and with `priceFor` the customer's custom prices.
 */
export function variantLookupQueryOptions(q: string, limit = 20, priceFor?: LookupPriceFor) {
  const query = q.trim()
  const customerId = priceFor?.customerId ?? null
  const date = priceFor?.date ?? null
  return queryOptions({
    queryKey: variantLookupKeys.search(query, limit, customerId, date),
    queryFn: ({ signal }) =>
      api.get<VariantLookup[]>('/product-variants/lookup', {
        signal,
        query: { q: query, limit, ...(customerId ? { customerId } : {}), ...(date ? { date } : {}) },
      }),
    enabled: query.length > 0,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })
}

export const purchaseLookupKeys = {
  all: ['product-variants', 'purchase-lookup'] as const,
  search: (q: string, limit: number) => [...purchaseLookupKeys.all, q, limit] as const,
}

/**
 * GET /product-variants/purchase-lookup: the same search for purchasing lines, with each variant's
 * last purchase cost and average cost (purchases.create, supplierReturns.create or products.read).
 */
export function purchaseVariantLookupQueryOptions(q: string, limit = 20) {
  const query = q.trim()
  return queryOptions({
    queryKey: purchaseLookupKeys.search(query, limit),
    queryFn: ({ signal }) =>
      api.get<PurchaseVariantLookup[]>('/product-variants/purchase-lookup', {
        signal,
        query: { q: query, limit },
      }),
    enabled: query.length > 0,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })
}

/** The variant's name in the UI language (the lookup label already joins product and variant names). */
export function variantLabel(variant: Pick<VariantLookup, 'label' | 'labelAr'>, language: Language): string {
  return language === 'ar' ? variant.labelAr?.trim() || variant.label : variant.label
}

/** Waits for typing to pause before searching (scanners type fast; people pause). */
function useDebounced(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay)
    return () => window.clearTimeout(timer)
  }, [value, delay])
  return debounced
}

interface VariantPickerBaseProps {
  /** The chosen variant id, or null. */
  value: string | null
  /** What the closed field shows when a variant is chosen (e.g. the line's product label). */
  label?: string | null
  placeholder: string
  /** Filters: the placeholder ("All products") is a real choice, shown in ink. */
  emptyIsChoice?: boolean
  disabled?: boolean
  size?: 'md' | 'sm'
  id?: string
  className?: string
  onBlur?: () => void
  /**
   * Where the cursor goes after a pick (a line's quantity), instead of back to the picker. Nothing
   * or null keeps it on the picker.
   */
  focusAfterPick?: () => HTMLElement | null | undefined
  'aria-label'?: string
  'aria-invalid'?: boolean
}

export type VariantPickerProps = VariantPickerBaseProps &
  (
    | {
        /** The selling lookup (default): rows show the selling price. */
        lookup?: 'sale'
        /** The document's customer and date, so a row brings the customer's custom prices. */
        priceFor?: LookupPriceFor | undefined
        onSelect: (variant: VariantLookup) => void
        /** Hide rows the caller cannot use (e.g. services on a stock document). */
        filter?: (variant: VariantLookup) => boolean
      }
    | {
        /** The purchasing lookup: rows show the last and average cost instead of the selling price. */
        lookup: 'purchase'
        priceFor?: undefined
        onSelect: (variant: PurchaseVariantLookup) => void
        filter?: (variant: PurchaseVariantLookup) => boolean
      }
  )

/**
 * Product picker for document lines: type a name, SKU or barcode and pick a sellable variant. Typing
 * on the closed field opens it with that text as the search, every keystroke kept, even a scanner's
 * burst that arrives before the list has rendered (`usePickerKeyboard`). A scanned barcode (typed
 * fast, then Enter) is picked as soon as its exact match arrives, even when Enter came before the
 * results.
 */
export function VariantPicker(props: VariantPickerProps) {
  const {
    value,
    label,
    placeholder,
    emptyIsChoice = false,
    disabled,
    size = 'md',
    id,
    className,
    onBlur,
    focusAfterPick,
    'aria-label': ariaLabel,
    'aria-invalid': ariaInvalid,
  } = props
  const { t } = useTranslation('catalog')
  const { t: tc } = useTranslation()
  const { language } = useLanguage()
  const field = useFieldControl({ id, 'aria-invalid': ariaInvalid || undefined })
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [highlighted, setHighlighted] = useState('')
  const pendingEnter = useRef<string | null>(null)
  const picked = useRef(false)
  const debounced = useDebounced(search, 200)
  const purchase = props.lookup === 'purchase'
  const typed = open && debounced.trim() !== ''
  const saleLookup = useQuery({
    ...variantLookupQueryOptions(debounced, 20, props.priceFor),
    enabled: typed && !purchase,
  })
  const purchaseLookup = useQuery({
    ...purchaseVariantLookupQueryOptions(debounced),
    enabled: typed && purchase,
  })
  const lookup: UseQueryResult<VariantLookup[]> = purchase ? purchaseLookup : saleLookup
  // The purchase lookup's rows are purchase rows: the caller's callbacks take them as such.
  const onSelect = props.onSelect as (variant: VariantLookup) => void
  const filter = props.filter as ((variant: VariantLookup) => boolean) | undefined
  const current = lookup.data && lookup.isPlaceholderData ? undefined : lookup.data
  const results = (lookup.data ?? []).filter((v) => !filter || filter(v))

  const setOpenState = (next: boolean) => {
    setOpen(next)
    if (!next) {
      setSearch('')
      pendingEnter.current = null
      onBlur?.()
    }
  }

  const choose = (variant: VariantLookup) => {
    picked.current = true
    onSelect(variant)
    setOpenState(false)
  }

  // Highlight the first row whenever the results change, so Enter picks the best match.
  const firstId = results[0]?.variantId ?? ''
  useEffect(() => {
    setHighlighted(firstId)
  }, [firstId])

  // A scanner's Enter that arrived before its results: pick the exact barcode/SKU match once loaded.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when a result set arrives
  useEffect(() => {
    const wanted = pendingEnter.current
    if (wanted === null || !current || debounced.trim() !== wanted) return
    pendingEnter.current = null
    const exact = current.find((v) => v.exact && (!filter || filter(v)))
    if (exact) choose(exact)
  }, [current, debounced])

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return
    const query = search.trim()
    if (!query) return
    // Results for exactly this text are not in yet: remember the Enter and let them decide.
    if (debounced.trim() !== query || lookup.isFetching || !current) {
      event.preventDefault()
      pendingEnter.current = query
    }
  }

  const keys = usePickerKeyboard({
    open,
    disabled,
    search,
    onSearchChange: setSearch,
    onOpenChange: setOpenState,
    // The scanner's Enter before the list took focus: the results for the whole code decide.
    onEnter: (typed) => {
      if (typed.trim()) pendingEnter.current = typed.trim()
    },
  })

  // After a pick the cursor moves on (to the line's quantity), instead of back to the picker.
  const onCloseAutoFocus = (event: Event) => {
    const next = picked.current ? focusAfterPick?.() : null
    picked.current = false
    if (!next) return
    event.preventDefault()
    next.focus()
    if (next instanceof HTMLInputElement) next.select()
  }

  const shown = value ? (label ?? '') : ''
  const typing = search.trim() !== ''

  return (
    <Popover.Root open={open} onOpenChange={setOpenState}>
      <Popover.Trigger asChild disabled={disabled}>
        <button
          type="button"
          {...field}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          onKeyDown={keys.onTriggerKeyDown}
          className={cn(
            controlBase,
            'inline-flex items-center justify-between gap-2 text-start',
            size === 'md' ? 'h-8 px-2.5 text-base' : 'h-7 px-2 text-sm',
            className,
          )}
        >
          <span className={cn('truncate', !shown && !emptyIsChoice && 'text-ink-faint')}>
            {shown || placeholder}
          </span>
          <ChevronsUpDown aria-hidden className="size-4 shrink-0 text-ink-muted" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          collisionPadding={8}
          className={cn(floatingPanel, 'w-(--radix-popover-trigger-width) min-w-80')}
          onOpenAutoFocus={keys.onOpenAutoFocus}
          onCloseAutoFocus={onCloseAutoFocus}
        >
          <Command shouldFilter={false} loop value={highlighted} onValueChange={setHighlighted}>
            <div className="flex items-center gap-2 border-b border-rule px-2.5">
              <Search aria-hidden className="size-4 shrink-0 text-ink-muted" />
              <Command.Input
                ref={keys.inputRef}
                value={search}
                onValueChange={setSearch}
                onKeyDown={onInputKeyDown}
                placeholder={t('lookup.search')}
                className="h-9 w-full bg-transparent text-sm text-ink outline-none"
              />
              <ScanBarcode aria-hidden className="size-4 shrink-0 text-ink-faint" />
            </div>
            <Command.List className="max-h-72 overflow-y-auto p-1" label={placeholder}>
              {!typing ? (
                <p className="px-2 py-3 text-sm text-ink-muted">{t('lookup.hint')}</p>
              ) : lookup.isPending || (lookup.isFetching && !lookup.data) ? (
                <Command.Loading>
                  <div className="px-2 py-3 text-sm text-ink-muted">{tc('loading')}</div>
                </Command.Loading>
              ) : lookup.isError ? (
                <p role="alert" className="px-2 py-3 text-sm text-ink-red">
                  {t('lookup.failed')}
                </p>
              ) : results.length === 0 ? (
                <p className="px-2 py-3 text-sm text-ink-muted">{t('lookup.empty')}</p>
              ) : (
                results.map((variant) => (
                  <Command.Item
                    key={variant.variantId}
                    value={variant.variantId}
                    onSelect={() => choose(variant)}
                    className={cn(menuItem, 'min-h-10 py-1 data-[selected=true]:bg-stamp-tint')}
                  >
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate">{variantLabel(variant, language)}</span>
                      <span className="flex min-w-0 gap-2 text-xs text-ink-muted">
                        {variant.sku ? (
                          <code dir="ltr" className="font-mono tabular-nums">
                            {variant.sku}
                          </code>
                        ) : null}
                        {variant.barcode ? (
                          <code dir="ltr" className="truncate font-mono tabular-nums">
                            {variant.barcode}
                          </code>
                        ) : null}
                        {variant.type === 'SERVICE' ? <span>{t('lookup.service')}</span> : null}
                      </span>
                    </span>
                    {purchase ? (
                      <PurchaseCost variant={variant as PurchaseVariantLookup} />
                    ) : (
                      <span className="shrink-0 text-end text-xs text-ink-muted">
                        <Amount value={variant.price} className="text-sm" />
                        <span className="block">{variant.unitSymbol}</span>
                      </span>
                    )}
                  </Command.Item>
                ))
              )}
            </Command.List>
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

/** A purchase lookup row's cost figures: the last purchase cost, else the average cost, per unit. */
function PurchaseCost({ variant }: { variant: PurchaseVariantLookup }) {
  const { t } = useTranslation('catalog')
  return (
    <span className="shrink-0 text-end text-xs text-ink-muted">
      {variant.lastPurchaseCost !== null ? (
        <span className="block">
          {t('lookup.lastCost')} <Amount value={variant.lastPurchaseCost} className="text-sm text-ink" />
        </span>
      ) : null}
      {variant.averageCost !== null ? (
        <span className="block">
          {t('lookup.averageCost')} <Amount value={variant.averageCost} />
        </span>
      ) : null}
      {variant.lastPurchaseCost === null && variant.averageCost === null ? (
        <span className="block">{t('lookup.noCost')}</span>
      ) : null}
      <span className="block">{variant.unitSymbol}</span>
    </span>
  )
}
